export const CLASS_LIST = ['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
export const SUBJECTS = ['English', 'Hindi', 'Mathematics', 'Science', 'Social Science', 'EVS', 'Sanskrit',
  'Computer Science', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Civics', 'Economics'];
export const EXAM_TITLES = ['Periodic Test 1', 'Periodic Test 2', 'Half-Yearly Examination', 'Annual Examination', 'Unit Test', 'Practice Paper'];

export const TYPE_META = {
  mcq: 'MCQ', fill_blank: 'Fill in the Blank', true_false: 'True / False', very_short: 'Very Short Answer',
  short: 'Short Answer', long: 'Long Answer', match: 'Match the Column', case_study: 'Case Study / Passage'
};
export const TYPES = Object.keys(TYPE_META);

export const DEFAULT_INSTRUCTIONS = [
  'All questions are compulsory unless internal choice is given.',
  'Read each question carefully before answering.',
  'Marks for each question are shown against it.',
  'Write neat and legible answers.'
];

let uid = 0;
export const newId = () => `${Date.now().toString(36)}-${(uid++).toString(36)}`;

export const blankQuestion = (type = 'short', marks = 1) => ({
  id: newId(), questionNumber: '', type, text: '', marks, options: type === 'mcq' ? ['', '', '', ''] : [],
  hasOrChoice: false, orQuestionText: '', correctAnswer: '', hint: '', rubric: '', difficulty: ''
});
export const blankSection = (n = 0) => ({
  id: newId(), sectionTitle: `Section ${String.fromCharCode(65 + (n % 26))}`, sectionInstructions: '', questions: []
});

export const defaultPaper = (master, user) => ({
  title: '', classId: user?.class || '', section: user?.section || '', subject: '', session: master?.session || '2026-27',
  maxMarks: 0, maxMarksLocked: false, timeAllowed: '2 Hours',
  headerConfig: {
    showLogo: true, logoUrl: master?.logoUrl || '/logo.png', schoolName: master?.schoolName || '',
    affiliation: master?.affiliation || '', examTitle: 'Periodic Test 1',
    customInstructions: master?.customInstructions?.length ? master.customInstructions : [...DEFAULT_INSTRUCTIONS],
    columnLayout: 'single', fontSize: 'md', watermarkText: '', showConfidential: false
  },
  sections: [blankSection(0)]
});

export const cloneSection = (s) => ({
  ...s, id: newId(), sectionTitle: `${s.sectionTitle} (Copy)`,
  questions: s.questions.map((q) => ({ ...q, id: newId(), options: [...(q.options || [])] }))
});

export const sectionTotal = (s) => s.questions.reduce((a, q) => a + (Number(q.marks) || 0), 0);
export const paperTotal = (sections) => sections.reduce((a, s) => a + sectionTotal(s), 0);

// Sequential numbering across all sections (display + save).
export const numbered = (sections) => {
  let n = 0;
  return sections.map((s) => ({ ...s, questions: s.questions.map((q) => ({ ...q, questionNumber: String(++n) })) }));
};

export const difficultyMix = (sections) => {
  const mix = { easy: 0, medium: 0, hard: 0, untagged: 0 };
  sections.forEach((s) => s.questions.forEach((q) => { mix[q.difficulty || 'untagged'] += Number(q.marks) || 0; }));
  return mix;
};

// Server paper -> editor state (adds ids)
export const fromServer = (p) => ({
  ...p,
  sections: (p.sections || []).map((s) => ({ ...s, id: newId(), questions: (s.questions || []).map((q) => ({ ...q, id: newId() })) }))
});

// ── Smart paste parser ────────────────────────────────────────────────────────
const Q_START = /^\s*(?:Q\.?\s*)?(\d{1,3})\s*[.):\-]\s+(.*)$/i;
const OPT_LINE = /^\s*\(?([A-Da-d])[.)]\s*(.+)$/;
const MARKS_RE = /[[(]\s*(\d+(?:\.\d+)?)\s*(?:marks?|mks?|m)\s*[\])]/i;
const ANS_LINE = /^\s*(?:ans(?:wer)?|key)\s*[:.\-]\s*(.+)$/i;
const OR_LINE = /^\s*[-–—\s]*OR[-–—\s]*$/i;

const guessType = (text, opts, marks) => {
  if (opts.length >= 2) return 'mcq';
  if (/_{3,}|\.{4,}/.test(text)) return 'fill_blank';
  if (/true\s*(or|\/)\s*false/i.test(text)) return 'true_false';
  if (/match\s+the\s+(column|following)/i.test(text)) return 'match';
  if (/read\s+the\s+(following\s+)?(passage|case)|case\s*study/i.test(text)) return 'case_study';
  if (marks >= 5) return 'long';
  if (marks >= 2) return 'short';
  return 'very_short';
};

export function parsePastedText(raw) {
  const lines = String(raw || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  lines.forEach((line) => {
    const m = line.match(Q_START);
    if (m) blocks.push({ lines: [m[2]] });
    else if (blocks.length && line.trim()) blocks[blocks.length - 1].lines.push(line.trim());
  });
  return blocks.map((b) => {
    let marks = 0; let answer = ''; let orText = ''; let inOr = false;
    const textLines = []; const options = [];
    b.lines.forEach((ln, i) => {
      const mm = ln.match(MARKS_RE);
      if (mm) { marks = marks || Number(mm[1]); ln = ln.replace(MARKS_RE, '').trim(); }
      if (OR_LINE.test(ln)) { inOr = true; return; }
      const a = ln.match(ANS_LINE);
      if (a) { answer = a[1].trim(); return; }
      if (i === 0 && /\(\s*[Aa]\s*\)/.test(ln) && /\(\s*[Bb]\s*\)/.test(ln)) {
        const parts = ln.split(/\s*\(\s*([A-Da-d])\s*\)\s*/);
        if (parts[0].trim()) textLines.push(parts[0].trim());
        for (let k = 1; k < parts.length; k += 2) if (parts[k + 1]) options.push(parts[k + 1].trim());
        return;
      }
      const o = ln.match(OPT_LINE);
      if (o && textLines.length) { options.push(o[2].trim()); return; }
      (inOr ? [orText] : textLines).push(ln);
      if (inOr) orText = `${orText}${orText ? ' ' : ''}${ln}`;
    });
    const text = textLines.join('\n').trim();
    const type = guessType(text, options, marks);
    return {
      ...blankQuestion(type, marks || 1), text, options: type === 'mcq' ? options : [], correctAnswer: answer,
      hasOrChoice: !!orText, orQuestionText: orText.trim()
    };
  }).filter((q) => q.text);
}
